<template>
  <DocSummaryPage
    :doc="book"
    :isLoadingDoc="isLoadingDoc"
    :isErrorLoadingDoc="isErrorLoadingDoc"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '../composables/useWait'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'
import {
  aiLabel13537Experiment,
  guideCompletion19383Variant,
} from '@/services/ab'

import { BookView } from '@/models/doc.book'
import { backLink } from '@/models/backlink'

import { offline } from '@/services/offline'
import { loadBookDetailFromNativeStorage } from '@/services/native.storage'
import DocSummaryPage from '@/app/components/doc/DocSummaryPage.vue'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const book = ref<Raw<BookView> | null>(null)

const abortController = ref<AbortController | null>(null)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component is about to
    // be navigated away from.
    // Has access to `this` component instance.
    if (book.value) {
      await book.value.updateReadTimeAndSavePosition()
      backLink.value.updateMetaTitle(from, book.value.title)
    }
    next()
  },
)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // If loadDocument() is still in progress, this signal will stop it.
    const wasLoading = abortController.value !== null
    abortController.value?.abort()

    if (to.query.reload === '1') {
      if (book.value && to.params.url_slug === from.params.url_slug) {
        // Mark as not started can return to the guide that is already open.
        // Its saved progress has changed, so replace the reader model instead of
        // just changing pages, and stop the old model's progress timers first.
        book.value.readingProgress?.destroy()
        await loadDocument(to)
      }
      // Remove the flag so another reset can request a fresh load. If this is
      // a different guide, the redirected navigation will load it normally.
      next({ ...to, query: { ...to.query, reload: undefined }, replace: true })
      return
    }

    if (book.value) {
      // Loading another guide checks the current route when it initializes
      // reading time. Let navigation finish before that initialization runs.
      void book.value.initPage(to, from, loadDocument)
      backLink.value.updateMetaTitle(from, book.value.title)
    } else if (wasLoading) {
      // We interrupted loadDocument() before it set book.value.
      // Restart it.
      await loadDocument(to)
    }
    next()
  },
)

onBeforeMount(async () => {
  wait.start('Loading Doc')
  await loadDocument(route)
  // When the reader first opens, loadDocument already fetches fresh data.
  // Remove the flag here too so a later reset can request another load.
  if (route.query.reload === '1') {
    await router.replace({ query: { ...route.query, reload: undefined } })
  }

  if (book.value && book.value.is_ai) {
    await aiLabel13537Experiment()
  }
})

async function _fetchDocument(
  route: RouteLocationNormalized,
  signal: AbortSignal,
): Promise<BookView> {
  if (offline.isOffline) {
    await loadBookDetailFromNativeStorage(route.params.url_slug as string)
  }
  const docData = await backend.getBook(route.params.url_slug as string, signal)
  const userData = await backend.getDocUserData(docData.id, signal)

  const docView = new BookView(docData, userData)
  docView.getPageFromRoute(route)
  return docView
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')

    abortController.value?.abort()
    abortController.value = new AbortController()
    const signal = abortController.value.signal

    // It's possible, though unlikely, that a new onBeforeRouteUpdate() will
    // arrive before loadDocument() completes. That would assign a new
    // book.value To avoid corner cases, we don't read book.value after
    // we store it. We refer only to the docView *we* created.
    const docView = await _fetchDocument(route, signal)
    book.value = docView

    // The initDocument() call doesn't accept an AbortSignal. But if we've
    // gotten this far, we don't need to abort. The onBeforeRouteUpdate()
    // will call initPage on the existing DocView.

    // Disabling the restoring when accessing the page from highlights page
    const restorePositionByReadingProgress =
      route.query?.highlight === undefined
    await docView.initDocument(restorePositionByReadingProgress)

    const completionVariant = guideCompletion19383Variant()
    if (completionVariant) {
      Mixpanel.trackCompletionExperimentGuideOpened(
        docView.id,
        completionVariant,
      )
    }

    if (!docView.isPreviewPage) {
      docView.scrollToTop()
    }
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else if (error.name === 'CanceledError') {
      // The onBeforeRouteUpdate() method cancelled us, but it will restart
      // the load, so we don't need to display an error.
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    abortController.value = null
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})
</script>
